<template>
  <div v-if="available" class="dtl" :class="{ 'is-new': !completed }">
    <button type="button" class="dtl__start" :title="$tn('dashboardWidgets.tour.launcherTitle')" @click="start">
      <i class="fas fa-graduation-cap" aria-hidden="true"></i>
      <span class="dtl__label">{{ $tn("dashboardWidgets.tour.launcher") }}</span>
    </button>
    <button
      type="button"
      class="dtl__close"
      :title="$tn('dashboardWidgets.tour.hide')"
      :aria-label="$tn('dashboardWidgets.tour.hide')"
      @click="dismiss"
    >
      <i class="fas fa-times" aria-hidden="true"></i>
    </button>
  </div>
</template>

<script>
/**
 * Botón del recorrido de widgets: está a mano durante WIDGETS_TOUR_DAYS desde la primera
 * vez que el usuario lo ve (fecha guardada en el back, vale en cualquier dispositivo), y
 * la primera vez arranca solo. La ✕ lo saca antes de tiempo.
 */
import { wencesApi } from "@/api/wencesApi";
import { startTour, isTourRunning } from "@/components/tour";
import { widgetsTour, WIDGETS_TOUR_ID, WIDGETS_TOUR_DAYS } from "./widgetsTour";

const DAY_MS = 24 * 60 * 60 * 1000;
const AUTOSTART_DELAY_MS = 1200;

export default {
  name: "DashboardTourLauncher",
  props: {
    /** El dashboard ya cargó (widgets y catálogo): el recorrido apunta a sus elementos. */
    ready: { type: Boolean, default: false },
  },
  data() {
    return { state: null, now: Date.now() };
  },
  computed: {
    available() {
      const s = this.state;
      if (!s || !s.firstSeenAt || s.dismissedAt) return false;
      return this.now - new Date(s.firstSeenAt).getTime() < WIDGETS_TOUR_DAYS * DAY_MS;
    },
    completed() {
      return !!(this.state && this.state.completedAt);
    },
  },
  watch: {
    ready(isReady) {
      if (isReady) this.maybeAutostart();
    },
  },
  async mounted() {
    this.state = await this.mark("seen");
    this.maybeAutostart();
  },
  beforeDestroy() {
    clearTimeout(this.autoTimer);
  },
  methods: {
    async mark(event) {
      try {
        const { data } = await wencesApi.post(`/api/users/tours/${WIDGETS_TOUR_ID}/${event}`);
        if (data && data.now) this.now = new Date(data.now).getTime();
        return (data && data.data) || null;
      } catch (e) {
        return this.state;
      }
    },
    maybeAutostart() {
      if (!this.ready || !this.available || this.state.autoStartedAt || this.autoTimer) return;
      this.autoTimer = setTimeout(async () => {
        if (isTourRunning()) return;
        this.state = await this.mark("autostarted");
        this.start();
      }, AUTOSTART_DELAY_MS);
    },
    start() {
      startTour(widgetsTour, {
        onFinish: async () => {
          this.state = await this.mark("completed");
        },
      });
    },
    async dismiss() {
      this.state = await this.mark("dismissed");
    },
  },
};
</script>

<style scoped>
.dtl {
  display: inline-flex;
  align-items: stretch;
  border: 1px solid rgba(var(--bs-primary-rgb), 0.45);
  border-radius: 999px;
  overflow: hidden;
  flex-shrink: 0;
  background: rgba(var(--bs-primary-rgb), 0.08);
}
.dtl.is-new { animation: dtl-glow 2.4s ease-in-out 3; }
@keyframes dtl-glow {
  0%, 100% { box-shadow: 0 0 0 0 rgba(var(--bs-primary-rgb), 0); }
  50% { box-shadow: 0 0 0 5px rgba(var(--bs-primary-rgb), 0.18); }
}
.dtl__start {
  border: 0;
  background: transparent;
  color: var(--bs-primary);
  font-size: 0.8rem;
  font-weight: 600;
  padding: 4px 10px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}
.dtl__close {
  border: 0;
  border-left: 1px solid rgba(var(--bs-primary-rgb), 0.3);
  background: transparent;
  color: var(--bs-primary);
  opacity: 0.7;
  padding: 0 8px;
  font-size: 0.7rem;
  cursor: pointer;
}
.dtl__close:hover { opacity: 1; }
@media (max-width: 767.98px) {
  .dtl__label { display: none; }
}
</style>
